import React, { useState } from 'react';
import { useNotify, useRecordContext, useRefresh } from 'react-admin';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Funeral } from '../../types';
import {
  markReceivedAtOffice,
  undoReceivedAtOffice,
  useRemainsPlan,
} from '../../deliveries/remainsApi';

/** What the line depends on, so it refetches when any of it changes on the case. */
export const remainsWatchKey = (record: Funeral): string =>
  [
    record.delivery_method,
    record.delivery_day,
    record.transfer_day,
    record.cremation_status,
    record.cremation_date,
    record.delivered_at,
    record.courier_call_number,
    record.courier_status,
    record.shipping_status,
  ].join('|');

/**
 * "Remains: At Lakeshore → in transfer Wed Nov 4 → at Cleo office → picked up",
 * above the Delivery card (T-230). The current step is bold. On an office pickup
 * the office step is also how staff record an arrival no courier reported.
 */
export const RemainsLine: React.FC = () => {
  const record = useRecordContext<Funeral>();
  const notify = useNotify();
  const refresh = useRefresh();
  const { plan, reload } = useRemainsPlan(
    record?.id,
    record ? remainsWatchKey(record) : '',
  );
  const [dialog, setDialog] = useState<'mark' | 'undo' | null>(null);
  const [saving, setSaving] = useState(false);

  const line = plan?.line;
  if (!record || !line) return null;

  const handleConfirm = async () => {
    setSaving(true);
    try {
      if (dialog === 'mark') await markReceivedAtOffice(record.id);
      else await undoReceivedAtOffice(record.id);
      setDialog(null);
      reload();
      refresh();
    } catch (error) {
      notify(`Couldn’t save that: ${(error as Error).message}`, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <p
        data-testid="remains-line"
        title={line.receivedBy ? `Marked by ${line.receivedBy}` : undefined}
        className="m-0 mb-3 px-0.5 pt-0.5 text-[12px] leading-[18px] text-muted-foreground break-words"
      >
        <span className="font-medium text-foreground">Remains:</span>{' '}
        {line.steps.map((step, i) => (
          <React.Fragment key={step.key}>
            {i > 0 && ' → '}
            {step.state === 'current' ? (
              <strong className="font-bold text-foreground">{step.text}</strong>
            ) : step.key === 'office' && line.canMarkReceived ? (
              <button
                type="button"
                title="Mark as received at the Cleo office"
                onClick={() => setDialog('mark')}
                className="underline decoration-dotted underline-offset-2 hover:text-foreground"
              >
                {step.text}
              </button>
            ) : (
              step.text
            )}
          </React.Fragment>
        ))}
        {line.canUndoReceived && (
          <>
            {' · '}
            <button
              type="button"
              onClick={() => setDialog('undo')}
              className="underline underline-offset-2 hover:text-foreground"
            >
              Undo
            </button>
          </>
        )}
        {line.problem && (
          <span className="block font-medium text-destructive">{line.problem}</span>
        )}
      </p>

      <Dialog open={!!dialog} onOpenChange={(next) => !saving && !next && setDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              {dialog === 'undo' ? 'Take this back?' : 'Received at the Cleo office?'}
            </DialogTitle>
            <DialogDescription>
              {dialog === 'undo'
                ? 'The remains go back to not yet at the office, and the transfer goes back on the list. The family is told nothing.'
                : 'Records that the remains are at the office. Earlier pickup dates open up and the courier reminder stops. The family is told nothing.'}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDialog(null)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={() => void handleConfirm()} disabled={saving}>
              {saving ? 'Saving…' : dialog === 'undo' ? 'Take back' : 'Mark received'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
